import JsonLd from '../JsonLd';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  faqs: FAQItem[];
}

const FAQSchema: React.FC<FAQSchemaProps> = ({ faqs }) => {
  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return <JsonLd data={faqData} />;
};

export default FAQSchema;

// Common FAQs for AI Governance
export const AI_GOVERNANCE_FAQS: FAQItem[] = [
  {
    question: "What is AI governance for boards?",
    answer: "AI governance for boards refers to the frameworks, policies, and oversight mechanisms that board directors use to ensure responsible development, deployment, and monitoring of artificial intelligence systems within their organizations. It includes establishing clear accountability, risk management protocols, ethical guidelines, and compliance with regulations like the EU AI Act."
  },
  {
    question: "Why do board directors need AI training?",
    answer: "Board directors need AI training because they have fiduciary duties to oversee organizational risks, including those posed by AI systems. Without proper understanding of AI capabilities, limitations, and risks, boards cannot effectively govern AI initiatives, evaluate strategic opportunities, ensure regulatory compliance, or protect stakeholder interests."
  },
  {
    question: "What is the EU AI Act and how does it affect boards?",
    answer: "The EU AI Act is the world's first comprehensive AI regulation, establishing rules for AI systems based on risk levels. It affects boards by requiring oversight of AI risk classifications, implementation of conformity assessments for high-risk AI, ensuring transparency requirements are met, and potentially facing significant penalties for non-compliance. Boards must ensure their organizations have proper governance structures to comply."
  },
  {
    question: "How long does the AI Governance Blueprint course take to complete?",
    answer: "The AI Governance Blueprint is a 12-module self-paced course designed to be completed in approximately 20 hours. Each module includes video content, readings, templates, and practical exercises. Most board directors complete the course within 4-8 weeks while balancing their professional responsibilities."
  },
  {
    question: "What certification do I receive after completing the course?",
    answer: "Upon completing the AI Governance Blueprint course, you receive an official certificate of completion that can be shared on LinkedIn and included in board credentials. The certificate validates your understanding of AI governance principles, risk management frameworks, and regulatory compliance requirements for board-level oversight."
  },
  {
    question: "What topics are covered in AI governance training for boards?",
    answer: "AI governance training for boards covers: fundamental AI concepts and terminology, AI risk identification and assessment frameworks, global regulatory landscape including the EU AI Act, ethical AI principles and explainability, data governance and privacy requirements, cybersecurity considerations for AI, board fiduciary duties for AI oversight, governance committee structures, and AI incident response planning."
  },
  {
    question: "How should boards structure AI oversight committees?",
    answer: "Boards should structure AI oversight through dedicated committees or subcommittees with clear mandates, regular reporting schedules, defined escalation procedures, and cross-functional representation. Key elements include establishing AI-specific charters, defining risk appetites, creating oversight metrics, and ensuring access to external expertise when needed."
  },
  {
    question: "What are the main AI risks boards should monitor?",
    answer: "Boards should monitor several categories of AI risks: ethical risks (bias, fairness, transparency), regulatory risks (compliance with AI laws), privacy risks (data protection), cybersecurity risks (adversarial attacks, model theft), operational risks (system failures, errors), financial risks (implementation costs, liability), reputational risks (public trust), and strategic risks (competitive positioning)."
  },
  {
    question: "What is explainable AI (XAI) and why does it matter for boards?",
    answer: "Explainable AI (XAI) refers to AI systems that can provide understandable explanations for their decisions and predictions. It matters for boards because explainability enables effective oversight, supports regulatory compliance, builds stakeholder trust, allows for meaningful human review of AI decisions, and helps identify potential bias or errors in AI systems."
  },
  {
    question: "How can boards assess their organization's AI governance maturity?",
    answer: "Boards can assess AI governance maturity through structured assessments that evaluate: existence of AI policies and procedures, clarity of roles and responsibilities, risk management frameworks, compliance monitoring systems, ethical guidelines implementation, incident response preparedness, board-level expertise, and alignment with regulatory requirements. BoardSight offers a free AI Readiness Assessment to help boards evaluate their current state."
  }
];
